import { Link } from "react-router-dom";
import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import AILabel from "@/components/AILabel";
import { Send, Trash2, Bot, User as UserIcon, Loader2 } from "lucide-react";

const SUGGESTED = [
  "What are the top 3 audit risks in this engagement?",
  "Which vendors show duplicate expense patterns?",
  "Summarize revenue concentration and cut-off exposure.",
  "Which discrepancies should I prioritize this week?",
];

// W1-06 — "[Methodology V5 §15.817, p.108]" → link that opens that version's PDF at the page.
function renderWithCitations(text, methLink) {
  const re = /\[Methodology V(\d+) §([^,\]]+), p\.(\d+)\]/g;
  const out = []; let last = 0; let m; let k = 0;
  while ((m = re.exec(text || "")) !== null) {
    out.push(text.slice(last, m.index));
    const href = methLink && String(methLink.version) === m[1] ? `/methodology?open=${methLink.id}&page=${m[3]}` : "/methodology";
    out.push(<Link key={k++} to={href} className="text-sky-700 underline" data-testid="assistant-meth-cite">{m[0]}</Link>);
    last = m.index + m[0].length;
  }
  out.push(text ? text.slice(last) : "");
  return out;
}

export default function Assistant() {
  const { t, lang } = useI18n();
  const { current } = useEngagement();
  const [messages, setMessages] = useState([]);
  const [methLink, setMethLink] = useState(null);
  useEffect(() => {
    if (!current?.id) { setMethLink(null); return; }
    api.get(`/engagements/${current.id}/methodology-link`, { _silent: true })
      .then((r) => setMethLink(r.data?.version || null)).catch(() => setMethLink(null));
  }, [current?.id]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const boxRef = useRef(null);

  const engId = current?.id;
  const load = useCallback(async () => {
    if (!engId) return;
    const r = await api.get(`/assistant/${engId}/history`);
    setMessages(r.data);
  }, [engId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages.length, sending]);

  const send = async (text) => {
    if (!current) return;
    const msg = (text ?? input).trim();
    if (!msg) return;
    setInput("");
    setSending(true);
    setMessages((prev) => [...prev, { id: `tmp-${Date.now()}`, role: "user", content: msg }]);
    try {
      await api.post(`/assistant/${current.id}/chat`, { message: msg });
      // Reload full history for consistency
      const h = await api.get(`/assistant/${current.id}/history`);
      setMessages(h.data);
    } catch (e) {
      setMessages((prev) => [...prev, { id: `err-${Date.now()}`, role: "assistant",
        content: "Sorry, something went wrong." }]);
    } finally {
      setSending(false);
    }
  };

  const clear = async () => {
    if (!current) return;
    await api.delete(`/assistant/${current.id}/history`);
    setMessages([]);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col space-y-4" data-testid="assistant-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">{t("assistant.title")}</h1>
            <AILabel />
          </div>
          <p className="text-sm text-muted-foreground">{t("assistant.subtitle")}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={clear} data-testid="assistant-clear">
          <Trash2 className="me-1 h-4 w-4" /> {t("assistant.clear")}
        </Button>
      </div>

      {/* audit-overflow: OK — chat shell; CardContent below owns overflow-y-auto */}
      <Card className="flex-1 flex flex-col overflow-hidden">
        <CardContent className="flex-1 overflow-y-auto p-0" ref={boxRef}>
          {messages.length === 0 && (
            <div className="p-8 space-y-4">
              <div className="mx-auto max-w-md text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-violet-100 text-violet-600 dark:bg-violet-950">
                  <Bot className="h-6 w-6" />
                </div>
                <div className="mt-3 text-sm font-medium">{t("assistant.emptyTitle")}</div>
                <div className="text-xs text-muted-foreground">{t("assistant.emptySub")}</div>
              </div>
              <div className="mx-auto grid max-w-2xl grid-cols-1 gap-2 md:grid-cols-2">
                {SUGGESTED.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    data-testid={`suggest-${i}`}
                    className="rounded-md border bg-white p-3 text-start text-sm text-slate-700 hover:border-violet-300 hover:bg-violet-50 dark:bg-slate-900 dark:text-slate-300"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-4 p-4">
            {messages.map((m) => (
              <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
                {m.role === "assistant" && (
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-600 dark:bg-violet-950">
                    <Bot className="h-4 w-4" />
                  </div>
                )}
                <div className={`max-w-[70%] rounded-lg px-3 py-2 text-sm ${
                  m.role === "user"
                    ? "bg-slate-900 text-white"
                    : "border bg-white text-slate-700 dark:bg-slate-900 dark:text-slate-200"
                }`}>
                  {/* Step 11d-7 · item 1: an AI error reply (e.g. "AI unavailable — contact Customer Service") reads in Arabic on an
                      Arabic screen — the server sends it as content_ar (ai_error_text.assistant_view); before, the English was shown */}
                  <div className="whitespace-pre-wrap">{renderWithCitations((lang === "ar" && m.content_ar) || m.content, methLink)}</div>
                </div>
                {m.role === "user" && (
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-700">
                    <UserIcon className="h-4 w-4" />
                  </div>
                )}
              </div>
            ))}
            {sending && (
              <div className="flex gap-3">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-600 dark:bg-violet-950">
                  <Bot className="h-4 w-4" />
                </div>
                <div className="rounded-lg border bg-white px-3 py-2 dark:bg-slate-900">
                  <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
                </div>
              </div>
            )}
          </div>
        </CardContent>
        <div className="border-t p-3">
          {current?.signed_off && (
            <div className="mb-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="assistant-locked-banner">
              {t("assistant.lockedBanner")}
            </div>
          )}
          <div className="flex gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={t("assistant.placeholder")}
              className="min-h-[44px] resize-none"
              data-testid="assistant-input"
              disabled={sending || !!current?.signed_off}
            />
            <Button onClick={() => send()} disabled={sending || !input.trim() || !!current?.signed_off} className="bg-slate-900 hover:bg-slate-800 self-end" data-testid="assistant-send">
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <div className="mt-2 text-[10px] text-muted-foreground">
            {t("assistant.disclaimer")}
          </div>
        </div>
      </Card>
    </div>
  );
}
